/**
 * CROEV Quiz v1 - Peak Footwear
 * Quiz funnel for barefoot shoe recommendations
 */

(function() {
  'use strict';

  // Pain area labels for display
  const PAIN_LABELS = {
    heel: 'Heel Pain',
    arch: 'Arch Pain',
    ball: 'Ball of Foot',
    toes: 'Toe Discomfort',
    ankle: 'Ankle Issues',
    joints: 'Knee/Hip/Back Pain'
  };

  // Quiz state management
  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.steps = Array.from(container.querySelectorAll('[data-quiz-step]'));
      this.progressBar = container.querySelector('[data-quiz-progress-bar]');
      this.wrapper = container.querySelector('.croev-quiz__wrapper');

      // State
      this.currentStep = 0;
      this.answers = {};
      this.history = [];

      // Settings from data attributes
      this.redirectUrl = container.dataset.redirectUrl || '';
      this.redirectType = container.dataset.redirectType || 'product';

      // Store active intervals and timeouts for cleanup
      this.activeIntervals = [];
      this.activeTimeouts = [];

      // Tracking
      this.webhookUrl = container.dataset.webhookUrl || '';
      this.sessionId = this.generateSessionId();
      this.utmParams = this.captureUTMs();
      this.startTime = Date.now();

      // Load saved state
      this.loadState();

      // Initialize
      this.init();
    }

    init() {
      this.bindEvents();
      this.showStep(this.currentStep);
      this.updateProgress();
    }

    bindEvents() {
      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        const option = e.target.closest('[data-quiz-option]');

        if (option && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          this.handleOptionClick(option);
        }

        // Allow Enter on continue buttons
        if (e.target.matches('[data-quiz-continue]') && e.key === 'Enter') {
          e.preventDefault();
          this.handleContinue();
        }
      });

      // Option clicks
      this.container.addEventListener('click', (e) => {
        const option = e.target.closest('[data-quiz-option]');
        if (option) {
          this.handleOptionClick(option);
        }

        const continueBtn = e.target.closest('[data-quiz-continue]');
        if (continueBtn) {
          this.handleContinue();
        }

        const backBtn = e.target.closest('[data-quiz-back]');
        if (backBtn) {
          this.handleBack();
        }

        const skipBtn = e.target.closest('[data-quiz-skip]');
        if (skipBtn) {
          this.handleSkip();
        }

        const ctaBtn = e.target.closest('[data-quiz-cta]');
        if (ctaBtn) {
          this.handleCTA(e);
        }
      });

      // Input changes
      this.container.addEventListener('input', (e) => {
        if (e.target.matches('[data-quiz-input]')) {
          this.handleInputChange(e.target);
        }
      });

      // Form submission
      this.container.addEventListener('submit', (e) => {
        if (e.target.matches('[data-quiz-form]')) {
          e.preventDefault();
          this.handleContinue();
        }
      });
    }

    handleOptionClick(option) {
      const step = this.steps[this.currentStep];
      const stepType = step.dataset.stepType;
      const isMulti = step.dataset.multiSelect === 'true';
      const questionId = step.dataset.questionId;
      const value = option.dataset.value;
      const autoAdvance = step.dataset.autoAdvance === 'true';

      if (isMulti) {
        // Toggle selection for multi-select
        const isSelected = option.classList.toggle('is-selected');
        option.setAttribute('aria-checked', isSelected);

        // Update answers array
        if (!this.answers[questionId]) {
          this.answers[questionId] = [];
        }

        const index = this.answers[questionId].indexOf(value);
        if (index > -1) {
          this.answers[questionId].splice(index, 1);
        } else {
          this.answers[questionId].push(value);
        }
      } else {
        // Single select - deselect others first
        step.querySelectorAll('[data-quiz-option]').forEach(opt => {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-checked', 'false');
        });
        option.classList.add('is-selected');
        option.setAttribute('aria-checked', 'true');
        this.answers[questionId] = value;

        // Auto-advance for single select (600ms for better UX feedback)
        if (autoAdvance) {
          const timeout = setTimeout(() => this.nextStep(), 600);
          this.activeTimeouts.push(timeout);
        }
      }

      // Enable/disable continue button
      this.updateContinueButton();
      this.saveState();

      // Track answer
      this.trackEvent('quiz_answer', {
        quiz_step_number: this.currentStep + 1,
        quiz_question_id: questionId,
        quiz_answer_value: Array.isArray(this.answers[questionId])
          ? this.answers[questionId].join(',')
          : this.answers[questionId]
      });
    }

    handleInputChange(input) {
      const step = this.steps[this.currentStep];
      const questionId = step.dataset.questionId;
      const inputType = input.type;

      // Validate email format
      if (inputType === 'email' && input.value) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = emailPattern.test(input.value);
        input.setCustomValidity(isValid ? '' : 'Please enter a valid email address');
      }

      this.answers[questionId] = input.value;
      this.updateContinueButton();
      this.saveState();
    }

    handleContinue() {
      const step = this.steps[this.currentStep];
      const questionId = step.dataset.questionId;
      const isRequired = step.dataset.required !== 'false';
      const stepType = step.dataset.stepType;

      // Skip validation for non-question steps (info, summary, offer, loading)
      const isQuestionStep = stepType && (stepType.startsWith('question_') || stepType === 'question_input');

      // Validate if required and is a question step
      if (isQuestionStep && isRequired && !this.hasAnswer(questionId)) {
        this.showValidationError(step);
        return;
      }

      this.nextStep();
    }

    handleBack() {
      if (this.history.length > 0) {
        const prevStep = this.history.pop();
        this.showStep(prevStep);
        this.saveState();
      }
    }

    handleSkip() {
      this.nextStep();
    }

    handleCTA(e) {
      e.preventDefault();

      // Build redirect URL with quiz answers
      let url = this.redirectUrl;

      if (url) {
        // Append quiz answers as query params (URLSearchParams handles encoding)
        const params = new URLSearchParams();

        // Add key answers (URLSearchParams automatically encodes)
        if (this.answers.age) params.set('age', this.answers.age);
        if (this.answers.main_concern) params.set('concern', this.answers.main_concern);
        if (this.answers.email) params.set('email', this.answers.email);

        // Add foot score
        params.set('score', this.calculateFootScore());

        const separator = url.includes('?') ? '&' : '?';
        url = url + separator + params.toString();

        const score = this.calculateFootScore();
        const durationSeconds = Math.round((Date.now() - this.startTime) / 1000);

        // GA4: quiz completion event
        this.trackEvent('quiz_complete', {
          quiz_score: score,
          quiz_duration_seconds: durationSeconds,
          quiz_steps_completed: this.history.length + 1
        });

        // Webhook: full submission data
        this.sendWebhook({
          event: 'quiz_complete',
          session_id: this.sessionId,
          timestamp: new Date().toISOString(),
          score: score,
          answers: this.answers,
          utm: this.utmParams,
          duration_seconds: durationSeconds,
          page_url: window.location.href,
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          steps_completed: this.history.length + 1,
          total_steps: this.steps.length
        });

        // Clear quiz state on completion
        try {
          localStorage.removeItem(`croev-quiz-${this.sectionId}`);
          localStorage.removeItem(`croev-quiz-utm-${this.sectionId}`);
        } catch (e) {
          // Ignore
        }

        window.location.href = url;
      }
    }

    nextStep() {
      // Save current step to history
      this.history.push(this.currentStep);

      // Find next step
      let nextIndex = this.currentStep + 1;

      // Skip steps based on conditions if needed
      while (nextIndex < this.steps.length) {
        const step = this.steps[nextIndex];
        if (this.shouldShowStep(step)) {
          break;
        }
        nextIndex++;
      }

      if (nextIndex < this.steps.length) {
        this.showStep(nextIndex);
      }

      this.saveState();
    }

    shouldShowStep(step) {
      // Add conditional logic here if needed
      // For now, show all steps
      return true;
    }

    showStep(index) {
      // Clean up any active intervals and timeouts from previous step
      this.cleanupIntervals();

      this.currentStep = index;

      // Hide all steps
      this.steps.forEach(step => {
        step.classList.remove('is-active');
      });

      // Show current step
      const step = this.steps[index];
      if (step) {
        step.classList.add('is-active');

        // Track step view
        const stepName = step.dataset.questionId || step.dataset.stepType;
        this.trackEvent('quiz_step_view', {
          quiz_step_number: index + 1,
          quiz_step_name: stepName,
          quiz_total_steps: this.steps.length
        });

        // Track quiz start on first step (fresh session only)
        if (index === 0 && this.history.length === 0) {
          this.trackEvent('quiz_start', {
            quiz_total_steps: this.steps.length,
            ...this.utmParams
          });
        }

        // Handle special step types
        const stepType = step.dataset.stepType;

        if (stepType === 'loading') {
          this.handleLoadingStep(step);
        } else if (stepType === 'info') {
          this.startReviewCarousel(step);
        } else if (stepType === 'summary_profile') {
          this.populateSummaryProfile(step);
        } else if (stepType === 'summary_results') {
          this.populateSummaryResults(step);
        } else if (stepType === 'summary_comparison') {
          this.populateSummaryComparison(step);
        } else if (stepType === 'offer') {
          this.populateOffer(step);
        }

        // Restore selected options
        this.restoreSelections(step);

        // Update continue button state
        this.updateContinueButton();
      }

      this.updateProgress();

      // Scroll to top - use wrapper on mobile (fixed layout), window on desktop
      if (this.wrapper) {
        this.wrapper.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration) || 3000;
      const style = step.dataset.loadingStyle || 'spinner';

      // Start review carousel if style is reviews
      if (style === 'reviews') {
        this.startReviewCarousel(step);
      }

      // Animate progress bar
      const progressBar = step.querySelector('[data-loading-bar]');
      if (progressBar) {
        progressBar.style.width = '0%';
        progressBar.style.transition = `width ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        requestAnimationFrame(() => {
          progressBar.style.width = '100%';
        });
      }

      // Animate checklist items
      const checkItems = step.querySelectorAll('[data-check-item]');
      if (checkItems.length) {
        const checkSvg = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7" fill="currentColor"/><path d="M5 8l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        const stepInterval = duration / (checkItems.length + 0.5);

        checkItems.forEach((item, i) => {
          // Reset
          item.classList.remove('is-active', 'is-done');

          // Activate this item
          const activateTimeout = setTimeout(() => {
            // Deactivate previous
            if (i > 0) {
              checkItems[i - 1].classList.remove('is-active');
              checkItems[i - 1].classList.add('is-done');
              const prevIcon = checkItems[i - 1].querySelector('[data-check-icon]');
              if (prevIcon) prevIcon.innerHTML = checkSvg;
            }
            item.classList.add('is-active');
          }, i * stepInterval);
          this.activeTimeouts.push(activateTimeout);

          // Complete last item before auto-advance
          if (i === checkItems.length - 1) {
            const doneTimeout = setTimeout(() => {
              item.classList.remove('is-active');
              item.classList.add('is-done');
              const icon = item.querySelector('[data-check-icon]');
              if (icon) icon.innerHTML = checkSvg;
            }, (i + 0.7) * stepInterval);
            this.activeTimeouts.push(doneTimeout);
          }
        });
      }

      // Auto-advance after duration
      const timeout = setTimeout(() => {
        this.nextStep();
      }, duration);

      this.activeTimeouts.push(timeout);
    }

    startReviewCarousel(step) {
      const slides = step.querySelectorAll('[data-review-slide]');
      const dots = step.querySelectorAll('[data-review-dot]');
      if (slides.length < 1) return;

      // Reset all slides, then show first
      slides.forEach(s => s.classList.remove('is-active'));
      dots.forEach(d => d.classList.remove('is-active'));
      slides[0].classList.add('is-active');
      if (dots.length) dots[0].classList.add('is-active');

      if (slides.length < 2) return;

      let currentSlide = 0;
      const interval = setInterval(() => {
        slides[currentSlide].classList.remove('is-active');
        if (dots.length) dots[currentSlide].classList.remove('is-active');
        currentSlide = (currentSlide + 1) % slides.length;
        slides[currentSlide].classList.add('is-active');
        if (dots.length) dots[currentSlide].classList.add('is-active');
      }, 3000);

      // Store interval for cleanup
      this.activeIntervals.push(interval);
    }

    cleanupIntervals() {
      // Clear all active intervals
      this.activeIntervals.forEach(interval => clearInterval(interval));
      this.activeIntervals = [];

      // Clear all active timeouts
      this.activeTimeouts.forEach(timeout => clearTimeout(timeout));
      this.activeTimeouts = [];
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.answers[questionId];

      if (!answer) return;

      if (Array.isArray(answer)) {
        // Multi-select
        answer.forEach(value => {
          const option = step.querySelector(`[data-quiz-option][data-value="${value}"]`);
          if (option) {
            option.classList.add('is-selected');
            option.setAttribute('aria-checked', 'true');
          }
        });
      } else {
        // Single select
        const option = step.querySelector(`[data-quiz-option][data-value="${answer}"]`);
        if (option) {
          option.classList.add('is-selected');
          option.setAttribute('aria-checked', 'true');
        }

        // Also restore input values
        const input = step.querySelector('[data-quiz-input]');
        if (input) input.value = answer;
      }
    }

    updateProgress() {
      const progressContainer = this.progressBar ? this.progressBar.parentElement : null;
      const totalSteps = this.steps.length;
      const progress = ((this.currentStep + 1) / totalSteps) * 100;

      if (this.progressBar) {
        this.progressBar.style.width = `${progress}%`;
      }

      if (progressContainer) {
        progressContainer.setAttribute('aria-valuenow', Math.round(progress));
      }
    }

    updateContinueButton() {
      const step = this.steps[this.currentStep];
      const continueBtn = step.querySelector('[data-quiz-continue]');
      const questionId = step.dataset.questionId;
      const isRequired = step.dataset.required !== 'false';
      const stepType = step.dataset.stepType;

      if (continueBtn) {
        // Always enable buttons for non-question steps (info, summary, offer, loading)
        const isQuestionStep = stepType && (stepType.startsWith('question_') || stepType === 'question_input');

        if (!isQuestionStep) {
          continueBtn.disabled = false;
          return;
        }

        // For required questions, check if answered
        if (isRequired) {
          const hasAnswer = this.hasAnswer(questionId);

          // For email inputs, also check validity
          if (stepType === 'question_input' && hasAnswer) {
            const input = step.querySelector('[data-quiz-input]');
            if (input && input.type === 'email') {
              continueBtn.disabled = !input.checkValidity();
            } else {
              continueBtn.disabled = !hasAnswer;
            }
          } else {
            continueBtn.disabled = !hasAnswer;
          }
        } else {
          // Optional questions - always enabled
          continueBtn.disabled = false;
        }
      }
    }

    hasAnswer(questionId) {
      const answer = this.answers[questionId];
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      return answer !== undefined && answer !== '';
    }

    showValidationError(step) {
      // Add shake animation or error state
      step.classList.add('has-error');
      setTimeout(() => step.classList.remove('has-error'), 500);
    }

    // Score calculation
    calculateFootScore() {
      let score = 75; // Base score

      // Pain frequency impact
      const painFreq = this.answers.pain_frequency;
      if (painFreq === 'daily') score -= 20;
      else if (painFreq === 'several') score -= 10;
      else if (painFreq === 'occasionally') score -= 5;

      // Pain areas count
      const painAreas = this.answers.pain_areas;
      if (Array.isArray(painAreas)) {
        score -= painAreas.length * 5;
      }

      // Hours on feet
      const hoursOnFeet = this.answers.time_on_feet;
      if (hoursOnFeet === '8+') score -= 10;
      else if (hoursOnFeet === '4-8') score -= 5;

      // Previous solutions
      const prevSolutions = this.answers.previous_solutions;
      if (Array.isArray(prevSolutions) && prevSolutions.includes('nothing')) {
        score -= 15;
      }

      // Pain impact
      const painImpact = this.answers.pain_impact;
      if (painImpact === 'significant') score -= 10;
      else if (painImpact === 'moderate') score -= 5;

      // Clamp score
      return Math.max(30, Math.min(100, score));
    }

    // Dynamic content population
    populateSummaryProfile(step) {
      const score = this.calculateFootScore();

      // Animate score number
      const scoreValue = step.querySelector('[data-score-value]');
      if (scoreValue) {
        this.animateNumber(scoreValue, 0, score, 1500);
      }

      // Animate SVG gauge arc
      this.animateGauge(step, score);

      // Update alert text with pain areas
      const alertText = step.querySelector('[data-alert-text]');
      if (alertText) {
        const painAreas = this.answers.pain_areas || [];
        const painLabels = painAreas.map(area => PAIN_LABELS[area] || area).join(', ');
        const template = alertText.dataset.template || 'Your footwear may be contributing to your {pain_areas}';
        alertText.textContent = template.replace('{pain_areas}', painLabels || 'discomfort');
      }

      // Animate breakdown bars
      this.updateScoreBreakdown(step, score);
    }

    animateGauge(step, score) {
      const gaugeFill = step.querySelector('[data-gauge-fill]');
      const gaugeContainer = step.querySelector('.croev-quiz__gauge');
      if (!gaugeFill || !gaugeContainer) return;

      // The arc path length for an 80-radius semicircle ≈ π * 80 ≈ 251.33
      const arcLength = Math.PI * 80;
      const targetOffset = arcLength * (1 - score / 100);

      // Set initial state: fully hidden
      gaugeFill.style.strokeDasharray = arcLength;
      gaugeFill.style.strokeDashoffset = arcLength;

      // Apply color class based on score
      gaugeContainer.classList.remove('croev-quiz__gauge--low', 'croev-quiz__gauge--mid', 'croev-quiz__gauge--high');
      if (score <= 45) {
        gaugeContainer.classList.add('croev-quiz__gauge--low');
      } else if (score <= 65) {
        gaugeContainer.classList.add('croev-quiz__gauge--mid');
      } else {
        gaugeContainer.classList.add('croev-quiz__gauge--high');
      }

      // Animate to target over ~1.5s
      requestAnimationFrame(() => {
        gaugeFill.style.transition = 'stroke-dashoffset 1.5s cubic-bezier(0.22, 1, 0.36, 1)';
        gaugeFill.style.strokeDashoffset = targetOffset;
      });
    }

    updateScoreBreakdown(step, score) {
      // Alignment based on current footwear
      const currentFootwear = this.answers.current_footwear;
      let alignmentLevel = 'fair';
      let alignmentPct = 55;
      if (currentFootwear === 'comfort' || currentFootwear === 'athletic') {
        alignmentLevel = 'high'; alignmentPct = 80;
      } else if (currentFootwear === 'dress' || currentFootwear === 'heels') {
        alignmentLevel = 'low'; alignmentPct = 25;
      }

      // Support based on pain areas count
      const painAreas = this.answers.pain_areas || [];
      let supportLevel = 'high';
      let supportPct = 80;
      if (painAreas.length > 2) { supportLevel = 'low'; supportPct = 25; }
      else if (painAreas.length > 0) { supportLevel = 'mid'; supportPct = 55; }

      // Movement based on activity level
      const activity = this.answers.activity_level;
      let movementLevel = 'mid';
      let movementPct = 55;
      if (activity === 'very_active') { movementLevel = 'high'; movementPct = 80; }
      else if (activity === 'sedentary') { movementLevel = 'low'; movementPct = 25; }

      const LEVEL_LABELS = { low: 'Poor', mid: 'Fair', high: 'Good' };

      // Populate each bar row
      const bars = [
        { key: 'alignment', pct: alignmentPct, level: alignmentLevel },
        { key: 'support', pct: supportPct, level: supportLevel },
        { key: 'movement', pct: movementPct, level: movementLevel }
      ];

      bars.forEach((bar, i) => {
        const row = step.querySelector(`[data-breakdown="${bar.key}"]`);
        if (!row) return;

        const valueEl = row.querySelector('.croev-quiz__bar-value');
        const fillEl = row.querySelector('.croev-quiz__bar-fill');

        if (valueEl) valueEl.textContent = LEVEL_LABELS[bar.level];
        if (fillEl) {
          // Reset
          fillEl.style.width = '0%';
          fillEl.className = 'croev-quiz__bar-fill croev-quiz__bar-fill--' + bar.level;
        }

        // Staggered reveal: fade in row, then animate bar width
        const delay = 400 + i * 250;
        const timeout = setTimeout(() => {
          row.classList.add('is-visible');
          // Animate fill after row is visible
          if (fillEl) {
            requestAnimationFrame(() => {
              fillEl.style.width = bar.pct + '%';
            });
          }
        }, delay);
        this.activeTimeouts.push(timeout);
      });
    }

    populateSummaryResults(step) {
      const today = new Date();
      const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Fill in dynamic dates on each card
      step.querySelectorAll('[data-timeline-date]').forEach(el => {
        const days = parseInt(el.dataset.timelineDate) || 0;
        const date = new Date(today);
        date.setDate(date.getDate() + days);
        el.textContent = formatDate(date);
      });

      // Staggered card reveal + bar fill animation
      const cards = step.querySelectorAll('[data-timeline-card]');
      cards.forEach((card, i) => {
        card.classList.remove('is-visible');
        const fill = card.querySelector('[data-fill-pct]');
        if (fill) fill.style.width = '0%';

        const timeout = setTimeout(() => {
          card.classList.add('is-visible');
          if (fill) {
            requestAnimationFrame(() => {
              fill.style.width = fill.dataset.fillPct + '%';
            });
          }
        }, 200 + i * 300);
        this.activeTimeouts.push(timeout);
      });
    }

    populateSummaryComparison(step) {
      // Personalize "before" text with pain areas
      const beforeText = step.querySelector('[data-before-text]');
      if (beforeText) {
        const painAreas = this.answers.pain_areas || [];
        const painCount = painAreas.length;
        if (painCount > 0) {
          const painLabels = painAreas.slice(0, 3).map(a => PAIN_LABELS[a] || a);
          beforeText.textContent = painLabels.join(', ') + ' limiting your daily life';
        }
      }

      // Staggered reveal: before card, after card, stats
      const cards = step.querySelectorAll('[data-vs-card]');
      const stats = step.querySelector('[data-vs-stats]');

      cards.forEach((card, i) => {
        card.classList.remove('is-visible');
        const timeout = setTimeout(() => card.classList.add('is-visible'), 200 + i * 250);
        this.activeTimeouts.push(timeout);
      });

      if (stats) {
        stats.classList.remove('is-visible');
        const timeout = setTimeout(() => stats.classList.add('is-visible'), 200 + cards.length * 250);
        this.activeTimeouts.push(timeout);
      }
    }

    populateOffer(step) {
      const score = this.calculateFootScore();
      const concern = this.answers.main_concern;
      const painAreas = this.answers.pain_areas || [];
      const frequency = this.answers.pain_frequency;

      // Concern-specific personalization copy
      const CONCERN_COPY = {
        plantar_fasciitis: {
          heading: 'Designed for Plantar Fasciitis Relief',
          text: 'The zero-drop design and flexible sole reduce tension on your plantar fascia, promoting natural healing with every step.'
        },
        bunions: {
          heading: 'Built for Bunion Comfort',
          text: 'The extra-wide toe box gives your toes room to spread naturally, reducing pressure on bunions without sacrificing support.'
        },
        foot_pain: {
          heading: 'Engineered to Relieve Foot Pain',
          text: 'The 4mm flexible sole strengthens intrinsic foot muscles while the zero-drop platform restores natural alignment.'
        },
        balance: {
          heading: 'Optimized for Better Balance',
          text: 'The zero-drop design and thin sole improve proprioception — your body\'s ability to sense ground contact for steadier footing.'
        },
        comfort: {
          heading: 'Made for All-Day Comfort',
          text: 'Ultra-lightweight with breathable materials and a natural foot shape — comfort you\'ll feel from the first step.'
        },
        none: {
          heading: 'Your Perfect Barefoot Shoe',
          text: 'Designed to promote natural foot movement, strengthen muscles, and keep you comfortable all day long.'
        }
      };

      // Populate personalized match card
      const headingEl = step.querySelector('[data-offer-match-heading]');
      const textEl = step.querySelector('[data-offer-match-text]');

      if (headingEl && textEl) {
        const copy = CONCERN_COPY[concern] || CONCERN_COPY.none;

        // Add pain area specifics to heading if available
        if (painAreas.length > 0 && concern && concern !== 'none') {
          const painLabels = painAreas.slice(0, 2).map(a => PAIN_LABELS[a] || a);
          headingEl.textContent = copy.heading;
          let matchText = copy.text;
          if (frequency === 'daily' || frequency === 'several') {
            matchText += ` With ${frequency === 'daily' ? 'daily' : 'frequent'} ${painLabels[0].toLowerCase()}, consistent wear delivers the fastest results.`;
          }
          textEl.textContent = matchText;
        } else {
          headingEl.textContent = copy.heading;
          textEl.textContent = copy.text;
        }
      }

      // Initialize gallery dots
      this.initOfferGallery(step);

      // Set CTA link to redirect URL with quiz params
      const cta = step.querySelector('[data-quiz-cta]');
      if (cta && this.redirectUrl) {
        const params = new URLSearchParams();
        if (this.answers.age) params.set('age', this.answers.age);
        if (this.answers.main_concern) params.set('concern', this.answers.main_concern);
        if (this.answers.email) params.set('email', this.answers.email);
        params.set('score', score);

        const separator = this.redirectUrl.includes('?') ? '&' : '?';
        cta.href = this.redirectUrl + separator + params.toString();
      }
    }

    initOfferGallery(step) {
      const gallery = step.querySelector('[data-offer-gallery]');
      if (!gallery) return;

      const track = gallery.querySelector('.croev-quiz__offer-gallery-track');
      const dotsContainer = gallery.querySelector('[data-offer-dots]');
      const slides = track.querySelectorAll('.croev-quiz__offer-gallery-slide');

      if (slides.length < 2 || !dotsContainer) return;

      // Create dots
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'croev-quiz__offer-gallery-dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Image ${i + 1}`);
        dot.addEventListener('click', () => {
          slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
        dotsContainer.appendChild(dot);
      });

      // Update active dot on scroll
      const dots = dotsContainer.querySelectorAll('.croev-quiz__offer-gallery-dot');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Array.from(slides).indexOf(entry.target);
            dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
          }
        });
      }, { root: track, threshold: 0.6 });

      slides.forEach(slide => observer.observe(slide));
    }

    animateNumber(element, start, end, duration) {
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        const current = Math.round(start + (end - start) * eased);

        element.textContent = current;

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }

    // Tracking methods
    generateSessionId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    }

    captureUTMs() {
      const params = new URLSearchParams(window.location.search);
      const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'];
      const utm = {};
      keys.forEach(key => {
        const val = params.get(key);
        if (val) utm[key] = val;
      });

      try {
        const stored = JSON.parse(localStorage.getItem(`croev-quiz-utm-${this.sectionId}`) || '{}');
        const merged = { ...stored, ...utm };
        if (Object.keys(merged).length > 0) {
          localStorage.setItem(`croev-quiz-utm-${this.sectionId}`, JSON.stringify(merged));
        }
        return merged;
      } catch (e) {
        return utm;
      }
    }

    trackEvent(eventName, eventData) {
      const params = {
        quiz_name: 'foot_health',
        quiz_session_id: this.sessionId,
        ...eventData
      };

      // Send directly to GA4 via gtag
      if (typeof gtag === 'function') {
        gtag('event', eventName, params);
      }

      // Also push to dataLayer for GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    }

    sendWebhook(data) {
      if (!this.webhookUrl) return;

      const payload = JSON.stringify(data);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(this.webhookUrl, new Blob([payload], { type: 'text/plain' }));
      } else {
        fetch(this.webhookUrl, {
          method: 'POST',
          body: payload,
          mode: 'no-cors',
          keepalive: true
        }).catch(() => {});
      }
    }

    // State persistence
    saveState() {
      const state = {
        currentStep: this.currentStep,
        answers: this.answers,
        history: this.history,
        startTime: this.startTime,
        timestamp: Date.now()
      };

      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(state));
      } catch (e) {
        console.warn('Could not save quiz state', e);
      }
    }

    loadState() {
      try {
        const stored = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (stored) {
          const state = JSON.parse(stored);

          // Check if state is less than 24 hours old
          const ageHours = (Date.now() - state.timestamp) / (1000 * 60 * 60);
          if (ageHours < 24) {
            this.currentStep = state.currentStep || 0;
            this.answers = state.answers || {};
            this.history = state.history || [];
            if (state.startTime) this.startTime = state.startTime;
          }
        }
      } catch (e) {
        console.warn('Could not load quiz state', e);
      }
    }

    resetQuiz() {
      this.currentStep = 0;
      this.answers = {};
      this.history = [];

      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        // Ignore
      }

      this.showStep(0);
    }
  }

  // Initialize on DOM ready
  function initQuizzes() {
    document.querySelectorAll('[data-croev-quiz]').forEach(container => {
      if (!container.dataset.initialized) {
        new CroevQuiz(container);
        container.dataset.initialized = 'true';
      }
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuizzes);
  } else {
    initQuizzes();
  }

  // Handle Shopify theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('[data-croev-quiz]');
    if (container) {
      container.dataset.initialized = '';
      new CroevQuiz(container);
      container.dataset.initialized = 'true';
    }
  });

  // Expose for external use
  window.CroevQuiz = CroevQuiz;

})();
